// src/services/authService.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signInWithPopup,
  signInWithCredential,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  updateCurrentUser,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
} from "firebase/auth";
import { getApps, initializeApp } from "firebase/app";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { getDeviceId } from "../utils/deviceId";
import { setAuthResponseData } from "./authResponseStorage";

const auth = getAuth();
const REDIRECT_RESULT_TIMEOUT_MS = 10000;

const isPopupFallbackError = (error) => {
  const code = error?.code || "";
  return (
    code === "auth/popup-blocked" ||
    code === "auth/operation-not-supported-in-this-environment" ||
    code === "auth/web-storage-unsupported"
  );
};

const getHostname = () => {
  if (typeof window === "undefined") return "";
  return String(window.location?.hostname || "").toLowerCase();
};

const isLocalHost = (hostname) => hostname === "localhost" || hostname === "127.0.0.1";

const isAndroid = () => {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIPhoneIPadIPod = /iPhone|iPad|iPod/i.test(ua);
  const isIPadOS13Plus =
    /Macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  return isIPhoneIPadIPod || isIPadOS13Plus;
};

export const resolveSocialAuthMode = () => {
  const host = getHostname();

  if (isLocalHost(host)) {
    return "popup";
  }

  if (isAndroid()) {
    return "popup";
  }

  if (isIOS()) {
    return "redirect";
  }

  return "redirect";
};

const getFirebaseHostedAuthDomain = () => {
  const projectId = String(process.env.REACT_APP_FIREBASE_PROJECT_ID || "").trim();
  if (!projectId) return "";
  return `${projectId}.firebaseapp.com`;
};

const isAndroidHostedRuntime = () => {
  const host = getHostname();
  return isAndroid() && !isLocalHost(host);
};

const getPopupAuthInstance = () => {
  // On Android hosted runtimes, use firebaseapp.com popup auth to avoid
  // custom-domain deep-link/PWA interception that can strand the popup on /__/auth/handler.
  if (!isAndroidHostedRuntime()) return auth;

  const firebaseHostedAuthDomain = getFirebaseHostedAuthDomain();
  if (!firebaseHostedAuthDomain) return auth;

  const popupAppName = "__social_popup_auth__";
  const existing = getApps().find((app) => app.name === popupAppName);

  const options = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: firebaseHostedAuthDomain,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };

  const popupApp = existing || initializeApp(options, popupAppName);
  console.info("[auth] Android popup auth domain override:", firebaseHostedAuthDomain);
  return getAuth(popupApp);
};

const getCredentialFromResult = (providerId, popupResult) => {
  if (providerId === "google.com") {
    return GoogleAuthProvider.credentialFromResult(popupResult);
  }
  if (providerId === "apple.com") {
    return OAuthProvider.credentialFromResult(popupResult);
  }
  return null;
};

const postFormData = async (url, data) => {
  console.info("[auth] POST start:", url);
  const response = await fetch(url, {
    method: "POST",
    body: data,
  });
  console.info("[auth] POST status:", url, response.status);
  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    console.error("[auth] POST failed:", url, response.status, errorText?.slice(0, 400));
    throw new Error(`API request failed (${response.status}) for ${url}`);
  }
  const text = await response.text();
  console.info("[auth] POST success:", url);
  return text;
};

const updateLastSignedInMarker = async (user) => {
  if (!user?.uid) return;
  try {
    const deviceId = await getDeviceId();
    await setDoc(
      doc(db, "users", user.uid),
      {
        lastSignedInAt: serverTimestamp(),
        lastSignedInDeviceId: deviceId || "",
      },
      { merge: true }
    );
  } catch (error) {
    console.warn("Failed to update lastSignedIn marker:", error);
  }
};

const handleThirdPartyAPICalls = async (user, password) => {
  console.info("[auth] third-party chain start for uid:", user?.uid);
  const token = await user.getIdToken();

  const socialLoginJWTURL = process.env.REACT_APP_SOCIAL_LOGIN_JWT_URL;
  const formData = new FormData();
  formData.append("fbtoken", token);
  formData.append("currentUser", JSON.stringify(user));
  formData.append("hasSignedUpWithINSSA", true);
  formData.append("password", password || "");

  await postFormData(socialLoginJWTURL, formData);

  const socialAuthenticateURL = process.env.REACT_APP_SOCIAL_AUTHENTICATE_URL;
  const authData = new FormData();
  authData.append("fbtoken", token);
  const authResponseData = await postFormData(socialAuthenticateURL, authData);
  const storageResult = setAuthResponseData(authResponseData);
  if (storageResult.storage !== "local") {
    console.info("[auth] authResponseData stored in fallback storage:", storageResult.storage);
  }
  await updateLastSignedInMarker(user);
  console.info("[auth] third-party chain complete for uid:", user?.uid);
};

// ---------- EMAIL/PASSWORD ----------
export const signUpUser = async (email, password) => {
  console.info("[auth] signUpUser start");
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  await handleThirdPartyAPICalls(user, password);
  console.info("[auth] signUpUser success uid:", user?.uid);
  return user;
};

export const signInUserWithEmailPassword = async (email, password) => {
  console.info("[auth] email sign-in start");
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  await handleThirdPartyAPICalls(user);
  console.info("[auth] email sign-in success uid:", user?.uid);
  return user;
};

const startProviderSignIn = async (provider) => {
  const mode = resolveSocialAuthMode();
  console.info("[auth] social mode:", mode, "provider:", provider.providerId);

  if (mode === "popup") {
    try {
      const popupAuth = getPopupAuthInstance();
      const popupResult = await signInWithPopup(popupAuth, provider);
      console.info("[auth] popup firebase sign-in success uid:", popupResult?.user?.uid);
      let signedInUser = popupResult.user;

      if (popupAuth !== auth) {
        console.info("[auth] bridging popup user into primary auth");
        const credential = getCredentialFromResult(provider.providerId, popupResult);
        if (credential) {
          const { user } = await signInWithCredential(auth, credential);
          signedInUser = user;
          console.info("[auth] bridge via signInWithCredential success uid:", user?.uid);
        } else {
          await updateCurrentUser(auth, popupResult.user);
          signedInUser = auth.currentUser || popupResult.user;
          console.info("[auth] bridge via updateCurrentUser success uid:", signedInUser?.uid);
        }
      }

      await handleThirdPartyAPICalls(signedInUser);
      console.info("[auth] popup provider flow complete uid:", signedInUser?.uid);
      return signedInUser;
    } catch (error) {
      console.error("[auth] popup provider flow failed:", error?.code, error?.message);
      if (isPopupFallbackError(error)) {
        const e = new Error("Popup sign-in is required in this context.");
        e.code = "auth/popup-required";
        throw e;
      }
      throw error;
    }
  }

  console.info("[auth] starting redirect flow for provider:", provider.providerId);
  await signInWithRedirect(auth, provider);
  return null;
};

// ---------- GOOGLE / APPLE ----------
export const startGoogleRedirect = async () => {
  const provider = new GoogleAuthProvider();
  return startProviderSignIn(provider);
};

export const startAppleRedirect = async () => {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return startProviderSignIn(provider);
};

/**
 * Handle the redirect result once after the app reloads.
 * Popup flows will never hit this because they do not reload.
 * @returns {Promise<import("firebase/auth").User|null>}
 */
export const handleRedirectResultOnce = async () => {
  console.info("[auth] handleRedirectResultOnce start");
  const result = await Promise.race([
    getRedirectResult(auth),
    new Promise((resolve) => {
      setTimeout(() => resolve(null), REDIRECT_RESULT_TIMEOUT_MS);
    }),
  ]);
  if (result?.user) {
    console.info("[auth] handleRedirectResultOnce got user uid:", result.user?.uid);
    await handleThirdPartyAPICalls(result.user);
    console.info("[auth] handleRedirectResultOnce complete");
    return result.user;
  }
  console.info("[auth] handleRedirectResultOnce no redirect user");
  return null;
};

export const finalizeRedirectFallbackIfNeeded = async (expectedRedirect) => {
  console.info("[auth] finalizeRedirectFallbackIfNeeded expected:", expectedRedirect);
  if (!expectedRedirect) return null;
  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.info("[auth] finalizeRedirectFallbackIfNeeded no currentUser");
    return null;
  }
  console.info("[auth] finalizeRedirectFallbackIfNeeded currentUser uid:", currentUser?.uid);
  await handleThirdPartyAPICalls(currentUser);
  console.info("[auth] finalizeRedirectFallbackIfNeeded complete");
  return currentUser;
};

// ---------- FRIENDLY ERROR + PASSWORD RESET ----------
export const getFriendlyErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/operation-not-allowed":
      return "Email/password accounts are not enabled.";
    case "auth/weak-password":
      return "Your password is too weak. Please use a stronger password.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/web-storage-unsupported":
    case "auth/operation-not-supported-in-this-environment":
      return "Private browsing or browser privacy settings may prevent sign-in from staying saved. Try a regular browser tab if this continues.";
    case "auth/user-not-found":
      return "No account found with this email address.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/popup-required":
      return "Social sign-in popup was blocked. Please allow popups and try again.";
    default:
      return "An unexpected error occurred. Please try again later.";
  }
};

export const sendPasswordResetEmail = async (email) => {
  return firebaseSendPasswordResetEmail(auth, email);
};
